import { useEffect, useRef, useState } from "react";
import api, { asItems, asTotal, API } from "@/lib/api";
import { money, dt, relTime, ORDER_LABELS, ORDER_COLORS, CHANNEL_LABELS } from "@/lib/format";
import { PageHeader, Loader, EmptyState, Pagination } from "@/components/Common";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Users, Search, GitMerge, Download, Upload, Plus, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

// Fix L (2026-09-22): base de datos de clientes — fichas editables, métricas de compra,
// segmentos, etiquetas e importación desde Excel/CSV. Las métricas las calcula el backend.
const SEGMENTOS = [
  { key: "", label: "Todos" },
  { key: "nuevo", label: "Nuevos" },
  { key: "recurrente", label: "Recurrentes" },
  { key: "vip", label: "VIP" },
  { key: "inactivo", label: "Inactivos" },
  { key: "sin_compras", label: "Sin compras" },
];
const SEGMENTO_LABEL = { nuevo: "Nuevo", recurrente: "Recurrente", vip: "VIP", inactivo: "Inactivo", sin_compras: "Sin compras" };
const SEGMENTO_COLOR = {
  nuevo: "bg-blue-50 text-blue-600", recurrente: "bg-emerald-50 text-emerald-600", vip: "bg-amber-50 text-amber-700",
  inactivo: "bg-gray-100 text-gray-600", sin_compras: "bg-muted text-muted-foreground",
};
const ORDENES = [
  { key: "nombre", label: "Nombre (A-Z)" },
  { key: "ultimo_pedido", label: "Compra más reciente" },
  { key: "total", label: "Más comprado (RD$)" },
  { key: "pedidos", label: "Más pedidos" },
];
const CAMPOS_IMPORT = [
  ["nombre", "Nombre"], ["apellido", "Apellido"], ["telefono", "Teléfono"], ["email", "Email"],
  ["usuario_instagram", "Instagram"], ["cedula_rnc", "Cédula / RNC"], ["direccion", "Dirección"],
  ["zona_envio", "Zona / sector"], ["cumpleanos", "Cumpleaños"], ["etiquetas", "Etiquetas"], ["notas", "Notas"],
];
const ESTADO_IMPORT = { nuevo: "Nuevo", existente: "Ya existe", omitido: "Sin datos", repetido_en_archivo: "Repetido en el archivo" };
const FICHA_VACIA = {
  nombre: "", telefono: "", usuario_instagram: "", email: "", cedula_rnc: "", cumpleanos: "",
  zona_envio: "", direcciones: [], etiquetas: [], notas: "", acepta_promociones: true,
};

const fichaAForm = (c) => ({
  ...FICHA_VACIA, ...c,
  direccionesTxt: (c.direcciones || []).join("\n"),
  etiquetasTxt: (c.etiquetas || []).join(", "),
});

export default function Customers() {
  const { user: me } = useAuth();
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [segmento, setSegmento] = useState("");
  const [etiqueta, setEtiqueta] = useState("todas");
  const [orden, setOrden] = useState("nombre");
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);
  const [resumen, setResumen] = useState(null);
  const LIMIT = 50;
  const debounceRef = useRef(null);

  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmarBorrar, setConfirmarBorrar] = useState(false);
  // Fix O (2026-09-24): borrar los datos de una clienta a pedido (política de privacidad, Ley 172-13).
  const [borrarDatosOpen, setBorrarDatosOpen] = useState(false);
  const [borrarDatosTexto, setBorrarDatosTexto] = useState("");
  const [borrandoDatos, setBorrandoDatos] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeQuery, setMergeQuery] = useState("");
  const [mergeCandidates, setMergeCandidates] = useState([]);

  const [importOpen, setImportOpen] = useState(false);
  const [importData, setImportData] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const filtros = () => {
    const p = {};
    if (qDebounced.trim()) p.q = qDebounced.trim();
    if (segmento) p.segmento = segmento;
    if (etiqueta !== "todas") p.etiqueta = etiqueta;
    return p;
  };

  const load = async () => {
    const r = await api.get("/customers", { params: { ...filtros(), orden, skip, limit: LIMIT } });
    setList(asItems(r));
    setTotal(asTotal(r));
  };
  const loadResumen = () => api.get("/customers/resumen").then((r) => setResumen(r.data)).catch(() => {});

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setQDebounced(q), 350);
    return () => clearTimeout(debounceRef.current);
  }, [q]);
  useEffect(() => { setSkip(0); }, [qDebounced, segmento, etiqueta, orden]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [qDebounced, segmento, etiqueta, orden, skip]);
  useEffect(() => { loadResumen(); }, []);

  const exportCsv = () => {
    const token = localStorage.getItem("ms_token");
    const params = new URLSearchParams(filtros());
    window.open(`${API}/customers/export?${params.toString()}&auth=${token}`, "_blank");
  };

  const open = async (c) => {
    const r = await api.get(`/customers/${c.id}`);
    setDetail(r.data);
    setForm(fichaAForm(r.data));
  };
  const nuevo = () => { setDetail({ nuevo: true }); setForm(fichaAForm(FICHA_VACIA)); };
  const cerrar = () => { setDetail(null); setForm(null); setConfirmarBorrar(false); };
  const puedeBorrar = (d) => me?.role === "admin" && d && !d.nuevo && !(d.pedidos?.length) && !(d.conversaciones?.length);
  const borrar = async () => {
    if (!confirmarBorrar) { setConfirmarBorrar(true); return; }
    try {
      await api.delete(`/customers/${detail.id}`);
      toast.success("Ficha eliminada"); cerrar(); load(); loadResumen();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo eliminar"); setConfirmarBorrar(false); }
  };
  const borrarDatos = async () => {
    setBorrandoDatos(true);
    try {
      const { data } = await api.post(`/customers/${detail.id}/borrar-datos`, { confirmar: borrarDatosTexto.trim() });
      toast.success(`Datos borrados: ${data.conversaciones} conversación(es), ${data.mensajes} mensaje(s) y ${data.archivos} archivo(s)`);
      setBorrarDatosOpen(false); setBorrarDatosTexto(""); cerrar(); load(); loadResumen();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudieron borrar los datos: revisá la ficha antes de reintentar");
      // Si el servidor igual terminó (p. ej. se cortó la conexión), la ficha ya muestra "Datos borrados".
      try { const r = await api.get(`/customers/${detail.id}`); setDetail(r.data); setForm(fichaAForm(r.data)); load(); } catch (e2) { /* sin cambios */ }
    } finally { setBorrandoDatos(false); }
  };
  const set = (campo) => (e) => setForm({ ...form, [campo]: e.target.value });

  const guardar = async () => {
    if (!form.nombre.trim()) return toast.error("El nombre es obligatorio");
    const payload = {
      nombre: form.nombre, telefono: form.telefono, usuario_instagram: form.usuario_instagram, email: form.email,
      cedula_rnc: form.cedula_rnc, cumpleanos: form.cumpleanos, zona_envio: form.zona_envio, notas: form.notas,
      acepta_promociones: !!form.acepta_promociones,
      direcciones: (form.direccionesTxt || "").split("\n").map((x) => x.trim()).filter(Boolean),
      etiquetas: (form.etiquetasTxt || "").split(",").map((x) => x.trim()).filter(Boolean),
    };
    setSaving(true);
    try {
      let ficha;
      if (detail?.nuevo) {
        const { data } = await api.post("/customers", payload);
        ficha = (await api.get(`/customers/${data.id}`)).data;
        toast.success("Cliente creado");
      } else {
        ficha = (await api.put(`/customers/${detail.id}`, payload)).data;
        toast.success("Ficha guardada");
      }
      setDetail(ficha);
      setForm(fichaAForm(ficha));
      load(); loadResumen();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(false); }
  };

  const openMerge = () => { setMergeOpen(true); setMergeQuery(""); setMergeCandidates([]); };
  useEffect(() => {
    if (!mergeOpen) return;
    api.get("/customers", { params: mergeQuery ? { q: mergeQuery } : {} })
      .then((r) => setMergeCandidates(asItems(r).filter((c) => c.id !== detail?.id)));
    /* eslint-disable-next-line */
  }, [mergeOpen, mergeQuery]);
  const doMerge = async (other) => {
    try {
      await api.post("/customers/fusionar", { principal_id: detail.id, secundario_id: other.id });
      toast.success(`Fusionado con ${other.nombre}`);
      setMergeOpen(false);
      const r = await api.get(`/customers/${detail.id}`);
      setDetail(r.data); setForm(fichaAForm(r.data)); load(); loadResumen();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const subirArchivo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setImportBusy(true); setImportResult(null);
    try {
      const { data } = await api.post("/customers/importar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportData(data);
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo leer el archivo"); }
    finally { setImportBusy(false); e.target.value = ""; }
  };
  const cambiarMapeo = async (campo, col) => {
    const mapeo = { ...importData.mapeo };
    if (col === "__ninguna") delete mapeo[campo]; else mapeo[campo] = col;
    try {
      const { data } = await api.post(`/customers/importar/${importData.id}/previsualizar`, { mapeo });
      setImportData(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const confirmarImport = async () => {
    setImportBusy(true);
    try {
      const { data } = await api.post(`/customers/importar/${importData.id}/confirmar`, { mapeo: importData.mapeo });
      setImportResult(data); setImportData(null); load(); loadResumen();
      toast.success(`Importación lista: ${data.creados} nuevos, ${data.actualizados} actualizados`);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setImportBusy(false); }
  };
  const cerrarImport = () => { setImportOpen(false); setImportData(null); setImportResult(null); };

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto">
      <PageHeader title="Clientes" subtitle="Base de datos de clientes: fichas, historial de compras y segmentos.">
        {me?.role === "admin" && (
          <Button variant="outline" onClick={() => setImportOpen(true)} className="rounded-full gap-2" data-testid="import-customers-btn"><Upload className="h-4 w-4" /> Importar</Button>
        )}
        <Button variant="outline" onClick={exportCsv} className="rounded-full gap-2" data-testid="export-customers-csv"><Download className="h-4 w-4" /> Exportar CSV</Button>
        <Button onClick={nuevo} className="rounded-full bg-primary hover:bg-primary/90 gap-2" data-testid="new-customer-btn"><Plus className="h-4 w-4" /> Nuevo cliente</Button>
      </PageHeader>

      <div className="flex gap-2 flex-wrap mb-2" data-testid="customer-segments">
        {SEGMENTOS.map((sg) => (
          <button key={sg.key || "todos"} onClick={() => setSegmento(sg.key)} data-testid={`segment-${sg.key || "todos"}`}
            className={`text-xs rounded-full px-3 py-1.5 flex items-center gap-1.5 ${segmento === sg.key ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
            {sg.label}
            {resumen && <span className="opacity-80">{sg.key ? (resumen.segmentos?.[sg.key] ?? 0) : resumen.total}</span>}
          </button>
        ))}
      </div>
      {resumen?.criterios && (
        <p className="text-[11px] text-muted-foreground mb-3">
          VIP: {resumen.criterios.vip_min_pedidos}+ pedidos o {money(resumen.criterios.vip_min_total)}+ comprados. Inactivo: más de {resumen.criterios.dias_inactivo} días sin comprar.
        </p>
      )}

      <div className="ms-card p-3 mb-4 flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Buscar por nombre, teléfono, Instagram, email o cédula…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 rounded-xl border-0 bg-muted" data-testid="customer-search" />
        </div>
        <Select value={etiqueta} onValueChange={setEtiqueta}>
          <SelectTrigger className="w-44 rounded-xl" data-testid="customer-filter-etiqueta"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">Todas las etiquetas</SelectItem>
            {(resumen?.etiquetas || []).map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={orden} onValueChange={setOrden}>
          <SelectTrigger className="w-48 rounded-xl" data-testid="customer-orden"><SelectValue /></SelectTrigger>
          <SelectContent>{ORDENES.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {!list ? <Loader /> : list.length === 0 ? (
        <EmptyState icon={Users} title="Sin clientes" subtitle={qDebounced || segmento || etiqueta !== "todas" ? "No hay clientes con esos filtros." : "Todavía no hay clientes registrados."} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {list.map((c) => (
            <button key={c.id} onClick={() => open(c)} data-testid={`customer-${c.id}`} className="ms-card ms-lift p-4 text-left">
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-full bg-accent flex items-center justify-center font-head font-semibold text-primary shrink-0">{c.nombre?.[0]?.toUpperCase() || "?"}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold truncate">{c.nombre}</p>
                    <Badge className={`rounded-full border-0 text-[10px] shrink-0 ${SEGMENTO_COLOR[c.segmento] || ""}`} data-testid={`customer-segmento-${c.id}`}>{SEGMENTO_LABEL[c.segmento] || c.segmento}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{c.telefono || c.usuario_instagram || c.email || "Sin contacto"}</p>
                  <p className="text-xs mt-1">
                    {c.metricas?.pedidos || 0} pedidos · {money(c.metricas?.total_comprado || 0)}
                    {c.metricas?.ultimo_pedido ? ` · último ${relTime(c.metricas.ultimo_pedido)}` : ""}
                  </p>
                  {c.etiquetas?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {c.etiquetas.slice(0, 4).map((e) => <span key={e} className="text-[10px] rounded-full bg-muted px-2 py-0.5">{e}</span>)}
                    </div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
      <Pagination skip={skip} limit={LIMIT} total={total} onChange={setSkip} />

      <Dialog open={!!detail} onOpenChange={(o) => !o && cerrar()}>
        <DialogContent className="w-[min(720px,95vw)] max-w-[min(720px,95vw)] max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden" data-testid="customer-detail-modal">
          {detail && form && (
            <>
              <DialogHeader className="shrink-0 border-b border-border px-5 py-4">
                <DialogTitle className="flex items-center gap-2 flex-wrap pr-6">
                  {detail.nuevo ? "Nuevo cliente" : detail.nombre}
                  {!detail.nuevo && <Badge className={`rounded-full border-0 ${SEGMENTO_COLOR[detail.segmento] || ""}`}>{SEGMENTO_LABEL[detail.segmento] || detail.segmento}</Badge>}
                  {!detail.nuevo && detail.origen && <span className="text-xs text-muted-foreground font-normal">Origen: {CHANNEL_LABELS[detail.origen] || detail.origen}</span>}
                  {detail.datos_borrados_en && <span className="text-xs text-muted-foreground font-normal" data-testid="customer-datos-borrados">Datos borrados el {dt(detail.datos_borrados_en)}</span>}
                </DialogTitle>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                {!detail.nuevo && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="customer-metricas">
                    {[["Pedidos", detail.metricas?.pedidos ?? 0], ["Total comprado", money(detail.metricas?.total_comprado)],
                      ["Ticket promedio", money(detail.metricas?.ticket_promedio)],
                      ["Último pedido", detail.metricas?.ultimo_pedido ? dt(detail.metricas.ultimo_pedido) : "—"]].map(([k, v]) => (
                      <div key={k} className="rounded-xl bg-accent/50 px-3 py-2">
                        <p className="text-[11px] text-muted-foreground">{k}</p>
                        <p className="text-sm font-semibold">{v}</p>
                      </div>
                    ))}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label className="text-xs">Nombre</Label><Input value={form.nombre} onChange={set("nombre")} className="rounded-xl mt-1" data-testid="customer-form-nombre" /></div>
                  <div><Label className="text-xs">Teléfono</Label><Input value={form.telefono} onChange={set("telefono")} placeholder="809-000-0000" className="rounded-xl mt-1" data-testid="customer-form-telefono" /></div>
                  <div><Label className="text-xs">Instagram</Label><Input value={form.usuario_instagram} onChange={set("usuario_instagram")} placeholder="@usuario" className="rounded-xl mt-1" data-testid="customer-form-instagram" /></div>
                  <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={set("email")} className="rounded-xl mt-1" data-testid="customer-form-email" /></div>
                  <div><Label className="text-xs">Cédula / RNC</Label><Input value={form.cedula_rnc} onChange={set("cedula_rnc")} className="rounded-xl mt-1" data-testid="customer-form-cedula" /></div>
                  <div><Label className="text-xs">Cumpleaños</Label><Input value={form.cumpleanos} onChange={set("cumpleanos")} placeholder="AAAA-MM-DD" className="rounded-xl mt-1" data-testid="customer-form-cumpleanos" /></div>
                  <div><Label className="text-xs">Zona de envío</Label><Input value={form.zona_envio} onChange={set("zona_envio")} className="rounded-xl mt-1" data-testid="customer-form-zona" /></div>
                  <div><Label className="text-xs">Etiquetas (separadas por coma)</Label><Input value={form.etiquetasTxt} onChange={set("etiquetasTxt")} placeholder="VIP, Mayorista…" className="rounded-xl mt-1" data-testid="customer-form-etiquetas" /></div>
                </div>
                <div><Label className="text-xs">Direcciones (una por línea)</Label><Textarea rows={2} value={form.direccionesTxt} onChange={set("direccionesTxt")} className="rounded-xl mt-1" data-testid="customer-form-direcciones" /></div>
                <div><Label className="text-xs">Notas</Label><Textarea rows={2} value={form.notas} onChange={set("notas")} className="rounded-xl mt-1" data-testid="customer-form-notas" /></div>
                <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2">
                  <span className="text-sm">Acepta recibir promociones</span>
                  <Switch checked={!!form.acepta_promociones} onCheckedChange={(v) => setForm({ ...form, acepta_promociones: v })} data-testid="customer-form-promos" />
                </div>

                {!detail.nuevo && (
                  <>
                    <div>
                      <h4 className="font-head font-semibold text-sm mb-2">Pedidos ({detail.pedidos?.length || 0})</h4>
                      {detail.pedidos?.length ? detail.pedidos.map((o) => (
                        <div key={o.id} className="flex items-center justify-between rounded-xl border border-border px-3 py-2 mb-1">
                          <span className="text-sm">#{o.numero} · {money(o.total)} <span className="text-xs text-muted-foreground">· {dt(o.created_at)}</span></span>
                          <Badge className={`rounded-full border-0 ${ORDER_COLORS[o.estado]}`}>{ORDER_LABELS[o.estado]}</Badge>
                        </div>
                      )) : <p className="text-sm text-muted-foreground">Sin pedidos.</p>}
                    </div>
                    <div>
                      <h4 className="font-head font-semibold text-sm mb-2">Conversaciones ({detail.conversaciones?.length || 0})</h4>
                      {detail.conversaciones?.length ? detail.conversaciones.map((cv) => (
                        <div key={cv.id} className="flex items-center justify-between rounded-xl border border-border px-3 py-2 mb-1">
                          <span className="text-sm">{CHANNEL_LABELS[cv.canal] || cv.canal}</span>
                          <span className="text-xs text-muted-foreground">{relTime(cv.last_message_at)}</span>
                        </div>
                      )) : <p className="text-sm text-muted-foreground">Sin conversaciones.</p>}
                    </div>
                  </>
                )}
              </div>
              <DialogFooter className="shrink-0 border-t border-border px-5 py-3 flex flex-col sm:flex-row gap-2">
                {!detail.nuevo && (
                  <Button variant="outline" className="rounded-full gap-1" onClick={openMerge} data-testid="open-merge-dialog"><GitMerge className="h-4 w-4" /> Fusionar con otro cliente</Button>
                )}
                {puedeBorrar(detail) && (
                  <Button variant="ghost" className="rounded-full text-red-500 hover:text-red-600 hover:bg-red-50" onClick={borrar} data-testid="customer-delete-btn">
                    {confirmarBorrar ? "Confirmar: eliminar ficha" : "Eliminar ficha"}
                  </Button>
                )}
                {me?.role === "admin" && !detail.nuevo && !detail.datos_borrados_en && (
                  <Button variant="ghost" className="rounded-full text-red-500 hover:text-red-600 hover:bg-red-50"
                    onClick={() => { setBorrarDatosTexto(""); setBorrarDatosOpen(true); }} data-testid="customer-borrar-datos-btn">
                    Borrar datos (a pedido)
                  </Button>
                )}
                <Button variant="outline" className="rounded-full" onClick={cerrar}>Cerrar</Button>
                <Button onClick={guardar} disabled={saving} className="rounded-full bg-primary hover:bg-primary/90" data-testid="customer-form-guardar">{saving ? "Guardando…" : "Guardar"}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
        <DialogContent className="max-w-sm max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Fusionar con otro cliente</DialogTitle>
            <DialogDescription>{detail?.nombre} quedará como contacto principal; se conserva el historial de ambos.</DialogDescription>
          </DialogHeader>
          <Input placeholder="Buscar cliente…" value={mergeQuery} onChange={(e) => setMergeQuery(e.target.value)} className="rounded-xl" data-testid="merge-search" />
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {mergeCandidates.map((c) => (
              <button key={c.id} onClick={() => doMerge(c)} className="w-full text-left rounded-xl px-3 py-2 hover:bg-accent" data-testid={`merge-candidate-${c.id}`}>
                <p className="text-sm font-medium">{c.nombre}</p>
                <p className="text-xs text-muted-foreground">{c.telefono || c.usuario_instagram || "Sin contacto"}</p>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={borrarDatosOpen} onOpenChange={(o) => !o && setBorrarDatosOpen(false)}>
        <DialogContent className="max-w-md" data-testid="borrar-datos-modal">
          <DialogHeader>
            <DialogTitle>Borrar los datos de {detail?.nombre}</DialogTitle>
            <DialogDescription>Solo cuando la clienta lo pide (política de privacidad). No se puede deshacer.</DialogDescription>
          </DialogHeader>
          <p className="text-sm font-medium" data-testid="borrar-datos-aviso">
            Antes de borrar, avisale por el chat que sus datos se borran ahora: después la conversación ya no está.
          </p>
          <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
            <li>Se borran sus conversaciones (mensajes, notas y seguimientos) y los archivos que mandó por el chat.</li>
            <li>La ficha y sus pedidos quedan como «Cliente eliminado», sin teléfono, dirección ni notas.</li>
            <li>Se conservan los montos de los pedidos, los pagos con sus comprobantes y las facturas (registros contables).</li>
            <li>Si tenía links de pago pendientes, se anulan.</li>
          </ul>
          <div>
            <Label className="text-xs">Escribí BORRAR para confirmar</Label>
            <Input value={borrarDatosTexto} onChange={(e) => setBorrarDatosTexto(e.target.value)} className="rounded-xl mt-1" data-testid="borrar-datos-confirmar" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setBorrarDatosOpen(false)}>Cancelar</Button>
            <Button onClick={borrarDatos} disabled={borrandoDatos || borrarDatosTexto.trim() !== "BORRAR"}
              className="rounded-full bg-red-600 hover:bg-red-700 text-white" data-testid="borrar-datos-submit">
              {borrandoDatos ? "Borrando…" : "Borrar datos"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={(o) => !o && cerrarImport()}>
        <DialogContent className="w-[min(760px,95vw)] max-w-[min(760px,95vw)] max-h-[90vh] overflow-y-auto" data-testid="import-customers-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-primary" /> Importar clientes</DialogTitle>
            <DialogDescription>
              Excel (.xlsx) o CSV. Se reconocen columnas como Nombre, Teléfono, Email, Instagram, Cédula, Dirección, Zona,
              Cumpleaños, Etiquetas y Notas. Si el cliente ya existe (mismo teléfono, email o Instagram), solo se completan los datos que le faltan.
            </DialogDescription>
          </DialogHeader>
          {importResult ? (
            <div className="rounded-xl bg-emerald-50 px-4 py-3 text-sm" data-testid="import-result">
              <p className="font-semibold">Importación terminada</p>
              <p>{importResult.creados} clientes nuevos · {importResult.actualizados} actualizados · {importResult.omitidos} omitidos</p>
            </div>
          ) : !importData ? (
            <div className="rounded-2xl border-2 border-dashed border-border p-8 text-center">
              <Input type="file" accept=".xlsx,.xls,.csv" onChange={subirArchivo} disabled={importBusy} className="rounded-xl max-w-xs mx-auto" data-testid="import-customers-file" />
              {importBusy && <p className="text-xs text-muted-foreground mt-2">Leyendo archivo…</p>}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-2 text-center" data-testid="import-conteos">
                <div className="rounded-xl bg-emerald-50 px-3 py-2"><p className="text-lg font-semibold">{importData.nuevos}</p><p className="text-[11px]">nuevos</p></div>
                <div className="rounded-xl bg-blue-50 px-3 py-2"><p className="text-lg font-semibold">{importData.existentes}</p><p className="text-[11px]">ya existen (se completan)</p></div>
                <div className="rounded-xl bg-muted px-3 py-2"><p className="text-lg font-semibold">{importData.omitidos}</p><p className="text-[11px]">omitidos</p></div>
              </div>
              <div>
                <Label className="text-xs mb-2 block">Columnas del archivo ({importData.total_filas} filas)</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {CAMPOS_IMPORT.map(([campo, label]) => (
                    <div key={campo} className="flex items-center gap-2">
                      <span className="text-xs w-28 shrink-0">{label}</span>
                      <Select value={importData.mapeo?.[campo] || "__ninguna"} onValueChange={(v) => cambiarMapeo(campo, v)}>
                        <SelectTrigger className="rounded-xl h-8 text-xs" data-testid={`import-map-${campo}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__ninguna">— No importar —</SelectItem>
                          {importData.columnas.map((col) => <SelectItem key={col} value={col}>{col}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-muted-foreground"><th className="py-1 pr-2">Nombre</th><th className="pr-2">Teléfono</th><th className="pr-2">Email</th><th className="pr-2">Zona</th><th>Estado</th></tr>
                  </thead>
                  <tbody>
                    {importData.preview.map((p, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="py-1 pr-2">{p.nombre || "—"}</td><td className="pr-2">{p.telefono}</td><td className="pr-2">{p.email}</td>
                        <td className="pr-2">{p.zona_envio}</td><td>{ESTADO_IMPORT[p.estado] || p.estado}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" className="rounded-full" onClick={() => setImportData(null)}>Elegir otro archivo</Button>
                <Button onClick={confirmarImport} disabled={importBusy || importData.nuevos + importData.existentes === 0} className="rounded-full bg-primary hover:bg-primary/90" data-testid="import-customers-confirm">
                  {importBusy ? "Importando…" : `Importar ${importData.nuevos + importData.existentes} clientes`}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
